/**
 * Operations System
 * 
 * Multi-stage missions with progress computed from session logs.
 * Replaces the leaderboard with a more immersive, mission-driven experience.
 */

import type { SessionLog, PTSessionLog } from './sessionStorage';
import { getSessionHistory } from './sessionStorage';
import { computeSetVolume } from './volumeTracking';
import { getCurrentUserId } from './anonymousAuth';

// ============================================================================
// TYPES
// ============================================================================

export type OperationStageType =
  | 'ruck_distance'
  | 'runwalk_distance'
  | 'strength_volume'
  | 'range_session';

export interface OperationStage {
  id: string;
  title: string;
  narrative: string;
  type: OperationStageType;
  target: number;              // meters for distance; volume units for strength; count for range
  unit: 'm' | 'mi' | 'units' | 'sessions';
  
  // Optional filters
  minLoadLbs?: number;         // e.g. "ruck must be >= 35 lbs"
  protocolIds?: string[];      // for range finisher
  
  // Locking (for final stage)
  lockedUntilStageIds?: string[];  // stage IDs that must be completed first

  // Engagement: staged narrative unlocks (25/50/75/100)
  intelDrops?: Array<{
    pct: 25 | 50 | 75 | 100;
    title: string;
    body: string;
  }>;
}

export interface OperationDefinition {
  id: string;
  name: string;
  season: 'winter' | 'spring' | 'summer' | 'fall';
  startDate: string;           // ISO date
  endDate: string;             // ISO date
  operationType?: 'linear' | 'territory'; // 'linear' = stage-based (Winter Forge), 'territory' = sector control (Spearhead)
  description: string;
  briefing: string;            // Commander's intent
  theme?: {
    key: string;               // e.g. "winter-forge"
    heroImage?: string;        // public path (e.g. "/operations/winter-forge-poster.png")
    mapImage?: string;         // optional "AO map" image used in Objective Map
    accent?: string;           // color or keyword
  };
  story?: {
    aoName: string;            // Area of operations
    enemyName: string;         // Opposing force
    enemyProfile: string;      // Short description
    threatLevel: 'low' | 'medium' | 'high';
    situation: string;         // SITREP-style narrative
    endState: string;          // What success looks like
    commsPlan: string;         // Flavor + UI text
  };
  stages: OperationStage[];
  rewards?: {
    badgeId?: string;
    xp?: number;
    titleUnlock?: string;
    callingCardUnlock?: string;       // Exclusive calling card
    emblemUnlock?: string;            // Exclusive emblem
    avatarUnlock?: string;            // Exclusive avatar
    archetypeBoost?: {                // Progress boost toward archetypes
      allArchetypes?: number;         // % boost to all archetype progress
      specificArchetype?: {
        id: string;
        boost: number;
      };
    };
    streakProtection?: number;        // Extra streak freezes granted
  };
}

export interface OperationProgress {
  operationId: string;
  userId: string;
  stageProgress: Record<string, number>;  // stageId → current value
  completedStageIds: string[];
  completedAt?: Date;
  lastComputedAt: Date;
}

// ============================================================================
// OPERATION DEFINITIONS
// ============================================================================

export const OPERATIONS: Record<string, OperationDefinition> = {
  'winter-forge-2026': {
    id: 'winter-forge-2026',
    name: 'Operation Winter Forge',
    season: 'winter',
    startDate: '2026-01-01',
    endDate: '2026-02-23',
    operationType: 'linear',
    description: 'A winter infiltration into a contested corridor—build the route picture, stage the ambush, then execute a precision engagement.',
    briefing: `MISSION: Insert to the ORP under load, recon the route network, stage equipment for an ambush, then execute a controlled engagement. If the log is dirty, the op is dirty. Range finisher unlocks after objectives 1–3 are complete.`,
    theme: {
      key: 'winter-forge',
      heroImage: '/operations/winter-forge-poster.png',
      mapImage: '/operations/winter-forge-map.png',
      accent: 'ice',
    },
    story: {
      aoName: 'BLACK PINE CORRIDOR',
      enemyName: 'SABLE DIRECTORATE',
      enemyProfile: 'A proxy force running drone sweeps and route interdictions. Light infantry with spotter teams, opportunistic QRF, and cheap ISR.',
      threatLevel: 'medium',
      situation: 'SIGINT indicates patrol density is increasing along the valley floor. Expect drone sweeps at dawn/dusk and a 12–18 minute QRF response window once contact is made. Weather is your ally and your enemy.',
      endState: 'ORP established, recon complete, ambush site staged, engagement executed—exfil clean with zero lapses in discipline.',
      commsPlan: 'COMMS/OPSEC: Maintain noise discipline. Log distance, time, and load. If it isn’t logged, it didn’t happen.',
    },
    stages: [
      {
        id: 'movement-to-orp',
        title: 'Movement to ORP',
        narrative: 'Insert under load. Establish the ORP without compromising pace or posture.',
        type: 'ruck_distance',
        target: 24140,  // 15 miles in meters
        unit: 'mi',
        minLoadLbs: 25,  // Minimum 25 lbs (ruck or ruck+plate carrier)
        intelDrops: [
          { pct: 25, title: 'DRONE SWEEP WINDOW', body: 'SIGINT: Directorate ISR cycles at dawn/dusk. Use weather cover; keep pace steady, not fast.' },
          { pct: 50, title: 'ROUTE INTERDICTION', body: 'Choke points are watched. If you feel form degrading, slow down—injury is compromise.' },
          { pct: 75, title: 'ORP PROXIMITY', body: 'You are in the corridor. Noise discipline. Hydrate. Control breathing. Do not spike the heart rate.' },
          { pct: 100, title: 'ORP ESTABLISHED', body: 'Objective Rally Point established. Hold. Reset feet. Prep for recon tasking.' },
        ],
      },
      {
        id: 'scouting-missions',
        title: 'Scouting Missions',
        narrative: 'Build the route picture. Cover ground quietly (run or walk) and map your limits.',
        type: 'runwalk_distance',
        target: 32187,  // 20 miles in meters
        unit: 'mi',
        intelDrops: [
          { pct: 25, title: 'GRID UPDATED', body: 'Your patrol pattern is forming. Keep it repeatable: same shoes, same surface, clean splits.' },
          { pct: 50, title: 'CONTACT LIKELY', body: 'Directorate spotters favor ridgelines. If fatigue is high, go easy—finish the week alive.' },
          { pct: 75, title: 'ROUTE PICTURE COMPLETE', body: 'You are close. Stay smooth. Do not sprint the last 10% and blow recovery.' },
          { pct: 100, title: 'RECON COMPLETE', body: 'Route network mapped. You know the ground now. Shift to staging.' },
        ],
      },
      {
        id: 'prep-ambush',
        title: 'Prep the Ambush',
        narrative: 'Stage equipment under stress. Every rep is logistics; every set is sustainment.',
        type: 'strength_volume',
        target: 60000,  // 60k lbs tonnage
        unit: 'units',
        intelDrops: [
          { pct: 25, title: 'LOAD PLAN SET', body: 'Keep sets clean. Stop chasing ego loads. The objective is volume without breakdown.' },
          { pct: 50, title: 'FORTIFICATION PHASE', body: 'Midpoint is where technique slips. Film a set if needed—quality is force multiplier.' },
          { pct: 75, title: 'STAGING COMPLETE', body: 'Finish strong. Leave the gym with gas in the tank—tomorrow still exists.' },
          { pct: 100, title: 'AMBUSH READY', body: 'Site staged. You’ve earned the range unlock. Precision is next.' },
        ],
      },
      {
        id: 'execute',
        title: 'Execute',
        narrative: 'Final engagement. Precision under fatigue. No shortcuts. No ego.',
        type: 'range_session',
        target: 1,
        unit: 'sessions',
        lockedUntilStageIds: ['movement-to-orp', 'scouting-missions', 'prep-ambush'],
        intelDrops: [
          { pct: 25, title: 'ROE CONFIRMED', body: 'Controlled reps, controlled shots. Slow is smooth.' },
          { pct: 50, title: 'HOLD STEADY', body: 'Fatigue lies. Stick to fundamentals: breath, sight picture, trigger.' },
          { pct: 75, title: 'FINAL CHECK', body: 'No rushed sets. No rushed strings. Clean execution beats fast execution.' },
          { pct: 100, title: 'OBJECTIVE COMPLETE', body: 'Engagement complete. Exfil clean. AAR follows.' },
        ],
      },
    ],
    rewards: {
      badgeId: 'winter-forge-complete',
      xp: 2500,                        // Increased from 1000
      titleUnlock: 'Winter Warrior',
      callingCardUnlock: 'winter-forge', // Matches CALLING_CARDS['winter-forge']
      emblemUnlock: 'winter-forge',      // Matches EMBLEMS['winter-forge']
      avatarUnlock: 'winter-warrior',    // Matches AVATARS['winter-warrior']
      archetypeBoost: {                // Progress boost toward archetypes
        allArchetypes: 0.05,           // +5% progress on all archetypes
      },
      streakProtection: 3,             // 3 extra streak freezes
    },
  },

  'spearhead-2026': {
    id: 'spearhead-2026',
    name: 'Operation Spearhead',
    season: 'spring',
    startDate: '2026-02-24',
    // Extended to match spearheadEngine.OPERATION_END. Was '2026-06-30' —
    // which silently took Spearhead "down" the moment that day arrived
    // because getActiveOperation() compares against UTC-midnight of the
    // end date. Keep this in lockstep with OPERATION_END in
    // lib/spearheadEngine.ts.
    endDate: '2026-11-30',
    operationType: 'territory',
    description: 'Dismantle a shadow syndicate operating across four contested sectors. EREBUS thrives on silence — every day you don\'t train, they consolidate. Break their network through relentless, multi-domain pressure.',
    briefing: 'MISSION: EREBUS — a decentralized ghost network — has embedded operatives across four operational domains. They don\'t fight head-on. They wait. They exploit gaps in your rhythm, capitalize on missed deployments, and reclaim ground while you sleep. Your weapon is consistency. Your mandate is total sector control.',
    theme: {
      key: 'spearhead',
      heroImage: '/operations/spearhead-hero.png',
      mapImage: '/operations/spearhead-map.svg',
      accent: '#3b82f6',
    },
    story: {
      aoName: 'SECTOR GRID ECHO-7',
      enemyName: 'EREBUS',
      enemyProfile: 'Decentralized shadow syndicate — ghost cells, no central command, thrives on operator inactivity.',
      threatLevel: 'high',
      situation: 'EREBUS operates through sleeper cells embedded across four domains. No uniforms, no flags. They activate when you go quiet and reclaim territory through attrition. Intelligence suggests they cannot sustain operations against consistent multi-domain pressure. Your presence is the weapon.',
      endState: 'All four sectors cleared. EREBUS network dismantled. Ghost cells neutralized through sustained operational tempo.',
      commsPlan: 'THEY FADE WHEN YOU SHOW UP',
    },
    stages: [], // Territory operations don't use linear stages
    rewards: {
      badgeId: 'spearhead-complete',
      xp: 3000,
      titleUnlock: 'Erebus Hunter',
      callingCardUnlock: 'territorial-dominance',
      emblemUnlock: 'spearhead-emblem',
    },
  },
};


// ============================================================================
// PROGRESS CALCULATION
// ============================================================================

/**
 * Compute operation progress from session history
 * This is the core logic that makes operations "just work" from existing logs
 */
export async function calculateOperationProgress(
  operationId: string,
  userId?: string
): Promise<OperationProgress | null> {
  const operation = OPERATIONS[operationId];
  if (!operation) {
    console.error(`Operation not found: ${operationId}`);
    return null;
  }

  const uid = userId || getCurrentUserId();
  if (!uid) {
    console.warn('No user ID for operation progress calculation');
    return null;
  }

  // Get sessions within operation window
  const startDate = new Date(operation.startDate);
  const endDate = new Date(operation.endDate);
  
  const allSessions = await getSessionHistory();
  const operationSessions = allSessions.filter(s => {
    const sessionDate = new Date(s.date);
    return sessionDate >= startDate && sessionDate <= endDate;
  });

  console.log(` Operation ${operationId}: ${operationSessions.length} sessions in window`);

  // Calculate progress for each stage
  const stageProgress: Record<string, number> = {};
  const completedStageIds: string[] = [];

  for (const stage of operation.stages) {
    let progress = 0;

    switch (stage.type) {
      case 'ruck_distance':
        progress = calculateRuckDistance(operationSessions, stage.minLoadLbs);
        break;
      
      case 'runwalk_distance':
        progress = calculateRunWalkDistance(operationSessions);
        break;
      
      case 'strength_volume':
        progress = calculateStrengthVolume(operationSessions);
        break;
      
      case 'range_session':
        progress = calculateRangeSessions(operationSessions, stage.protocolIds);
        break;
    }

    stageProgress[stage.id] = progress;
    
    if (progress >= stage.target) {
      completedStageIds.push(stage.id);
    }
  }

  // Check if operation is complete (guard against empty stages returning vacuous true)
  const allStagesComplete = operation.stages.length > 0 && operation.stages.every(s => completedStageIds.includes(s.id));
  const completedAt = allStagesComplete ? new Date() : undefined;

  return {
    operationId,
    userId: uid,
    stageProgress,
    completedStageIds,
    completedAt,
    lastComputedAt: new Date(),
  };
}

/**
 * Calculate ruck distance (meters) from sessions
 * Only count rucks that meet minimum load requirement
 */
function calculateRuckDistance(sessions: SessionLog[], minLoadLbs?: number): number {
  let totalMeters = 0;

  sessions.forEach(session => {
    if ((session.sessionType === 'pt' || session.sessionType === 'assessment') && 'exercises' in session) {
      const ptSession = session as PTSessionLog;
      
      ptSession.exercises?.forEach(ex => {
        const nameLower = (ex.exerciseName || '').toLowerCase();
        const formatLower = (ex.format || '').toLowerCase();
        const isRuckLike =
          ex.exerciseType === 'ruck' ||
          formatLower.includes('ruck') ||
          nameLower.includes('ruck');

        if (isRuckLike && ex.actualDistance) {
          // Check load requirement
          const totalLoad = (ex.actualLoad || 0) + ((ex as any).plateCarrierWeight || 0);
          
          if (!minLoadLbs || totalLoad >= minLoadLbs) {
            totalMeters += ex.actualDistance;
          }
        }
      });
    }
  });

  return totalMeters;
}

/**
 * Calculate run/walk distance (meters) from cardio exercises
 */
function calculateRunWalkDistance(sessions: SessionLog[]): number {
  let totalMeters = 0;

  sessions.forEach(session => {
    if ((session.sessionType === 'pt' || session.sessionType === 'assessment') && 'exercises' in session) {
      const ptSession = session as PTSessionLog;
      
      ptSession.exercises?.forEach(ex => {
        // Count cardio exercises that are NOT loaded (no actualLoad or actualLoad === 0)
        const nameLower = (ex.exerciseName || '').toLowerCase();
        const formatLower = (ex.format || '').toLowerCase();
        const isRuckLike = formatLower.includes('ruck') || nameLower.includes('ruck');

        if (ex.exerciseType === 'cardio' && ex.actualDistance && !ex.actualLoad && !isRuckLike) {
          totalMeters += ex.actualDistance;
        }
      });
    }
  });

  return totalMeters;
}

/**
 * Calculate strength volume (tonnage) from PT/assessment sessions
 */
function calculateStrengthVolume(sessions: SessionLog[]): number {
  let totalVolume = 0;

  sessions.forEach(session => {
    if ((session.sessionType === 'pt' || session.sessionType === 'assessment') && 'exercises' in session) {
      const ptSession = session as PTSessionLog;
      
      ptSession.exercises?.forEach(ex => {
        // Traditional weight training volume
        if (ex.sets) {
          ex.sets.forEach(set => {
            if (set.weight && set.reps) {
              totalVolume += computeSetVolume(set, ex.exerciseName);
            }
          });
        }
      });
    }
  });

  return totalVolume;
}

/**
 * Count range sessions (optionally filtered by protocol)
 */
function calculateRangeSessions(sessions: SessionLog[], protocolIds?: string[]): number {
  let count = 0;

  sessions.forEach(session => {
    if (session.sessionType === 'range') {
      // If no protocol filter, count all range sessions
      if (!protocolIds || protocolIds.length === 0) {
        count++;
      }
      // Otherwise, check if session matches any of the allowed protocols
      else if ('protocolId' in session && protocolIds.includes(session.protocolId as string)) {
        count++;
      }
    }
  });

  return count;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get the currently active operation (if any)
 */
export function getActiveOperation(): OperationDefinition | null {
  const now = new Date();

  // ARG ramp-up: Spearhead is hidden in-app and replaced with the terminal breadcrumb.
  // Re-enable by flipping SPEARHEAD_ENABLED in lib/spearheadEngine.ts.
  // Note: We import lazily to avoid circular dependencies.
  let spearheadEnabled = false;
  try {
    // Deliberate require(): lazy + inside a try so a missing module degrades
    // to `false` rather than breaking the import graph.
    spearheadEnabled = require('./spearheadEngine').SPEARHEAD_ENABLED;
  } catch { /* default false */ }

  for (const op of Object.values(OPERATIONS)) {
    // Skip territory ops (currently only Spearhead) when feature flag is off
    if (!spearheadEnabled && op.operationType === 'territory') continue;

    const start = new Date(op.startDate + 'T00:00:00');
    // Inclusive of the entire end day. `new Date('YYYY-MM-DD')` parses to
    // UTC midnight, so a bare end date made the op go inactive partway
    // through its final day (and in negative-UTC zones, a day early).
    const end = new Date(op.endDate + 'T23:59:59');

    if (now >= start && now <= end) {
      return op;
    }
  }

  return null;
}

/**
 * Check if a stage is locked (waiting for other stages to complete)
 */
export function isStageUnlocked(
  stage: OperationStage,
  completedStageIds: string[]
): boolean {
  if (!stage.lockedUntilStageIds || stage.lockedUntilStageIds.length === 0) {
    return true;
  }
  
  return stage.lockedUntilStageIds.every(id => completedStageIds.includes(id));
}

/**
 * Format progress value for display
 */
export function formatStageProgress(
  current: number,
  target: number,
  unit: OperationStage['unit']
): string {
  switch (unit) {
    case 'm':
      // Convert meters to miles for display
      const currentMiles = (current / 1609.34).toFixed(1);
      const targetMiles = (target / 1609.34).toFixed(0);
      return `${currentMiles} / ${targetMiles} mi`;
    
    case 'mi':
      const cMi = (current / 1609.34).toFixed(1);
      const tMi = (target / 1609.34).toFixed(0);
      return `${cMi} / ${tMi} mi`;
    
    case 'units':
      // Format volume as K if > 1000
      const cVol = current >= 1000 ? `${(current / 1000).toFixed(1)}K` : current.toString();
      const tVol = target >= 1000 ? `${(target / 1000).toFixed(0)}K` : target.toString();
      return `${cVol} / ${tVol} lbs`;
    
    case 'sessions':
      return `${current} / ${target}`;
    
    default:
      return `${current} / ${target}`;
  }
}

/**
 * Calculate percentage complete for a stage
 */
export function getStagePercentage(current: number, target: number): number {
  return Math.min(100, Math.round((current / target) * 100));
}

/**
 * Award operation rewards when completed
 * This should be called when an operation is completed for the first time
 */
export async function awardOperationRewards(
  operationId: string,
  userId: string
): Promise<{ success: boolean; awarded: string[] }> {
  const operation = OPERATIONS[operationId];
  if (!operation || !operation.rewards) {
    return { success: false, awarded: [] };
  }

  const awarded: string[] = [];

  try {
    // Dynamic import to avoid circular dependencies
    const { loadProgressionState, saveProgressionState, unlockCosmetic } = await import('./progressionService');
    const state = await loadProgressionState(userId);

    // Check if already awarded (store in milestonesUnlocked)
    const awardKey = `operation-${operationId}-complete`;
    const previouslyAwarded = state.milestonesUnlocked?.includes(awardKey);

    // Award XP/Intel (only on first award — skip if re-running)
    if (!previouslyAwarded && operation.rewards.xp) {
      state.lifetimeIntel = (state.lifetimeIntel || 0) + operation.rewards.xp;
      state.levelIntel = (state.levelIntel || 0) + operation.rewards.xp;
      awarded.push(`+${operation.rewards.xp} Intel`);
      console.log(` Awarded ${operation.rewards.xp} Intel for completing ${operationId}`);
    }

    // Award title
    if (operation.rewards.titleUnlock) {
      if (!state.unlockedTitles) state.unlockedTitles = ['Recruit'];
      if (!state.unlockedTitles.includes(operation.rewards.titleUnlock)) {
        state.unlockedTitles.push(operation.rewards.titleUnlock);
        awarded.push(`Title: ${operation.rewards.titleUnlock}`);
        console.log(` Unlocked title: ${operation.rewards.titleUnlock}`);
      }
    }

    // Award calling card
    if (operation.rewards.callingCardUnlock) {
      if (!state.unlockedCallingCards) state.unlockedCallingCards = ['default'];
      if (!state.unlockedCallingCards.includes(operation.rewards.callingCardUnlock)) {
        state.unlockedCallingCards.push(operation.rewards.callingCardUnlock);
        awarded.push(`Calling Card: ${operation.rewards.callingCardUnlock}`);
        console.log(` Unlocked calling card: ${operation.rewards.callingCardUnlock}`);
      }
    }

    // Award emblem
    if (operation.rewards.emblemUnlock) {
      if (!state.unlockedEmblems) state.unlockedEmblems = ['default'];
      if (!state.unlockedEmblems.includes(operation.rewards.emblemUnlock)) {
        state.unlockedEmblems.push(operation.rewards.emblemUnlock);
        awarded.push(`Emblem: ${operation.rewards.emblemUnlock}`);
        console.log(` Unlocked emblem: ${operation.rewards.emblemUnlock}`);
      }
    }

    // Award avatar
    if (operation.rewards.avatarUnlock) {
      if (!state.unlockedAvatars) state.unlockedAvatars = ['default'];
      if (!state.unlockedAvatars.includes(operation.rewards.avatarUnlock)) {
        state.unlockedAvatars.push(operation.rewards.avatarUnlock);
        awarded.push(`Avatar: ${operation.rewards.avatarUnlock}`);
        console.log(` Unlocked avatar: ${operation.rewards.avatarUnlock}`);
      }
    }

    // Award badge (store in milestonesUnlocked)
    if (operation.rewards.badgeId) {
      if (!state.milestonesUnlocked) state.milestonesUnlocked = [];
      if (!state.milestonesUnlocked.includes(operation.rewards.badgeId)) {
        state.milestonesUnlocked.push(operation.rewards.badgeId);
        awarded.push(`Badge: ${operation.rewards.badgeId}`);
        console.log(` Unlocked badge: ${operation.rewards.badgeId}`);
      }
    }

    // Award streak protection
    if (operation.rewards.streakProtection) {
      // Store extra freezes - this would need additional tracking
      awarded.push(`+${operation.rewards.streakProtection} Streak Freezes`);
      console.log(` Awarded ${operation.rewards.streakProtection} extra streak freezes`);
    }

    // Mark as awarded (idempotent)
    if (!state.milestonesUnlocked) state.milestonesUnlocked = [];
    if (!state.milestonesUnlocked.includes(awardKey)) {
      state.milestonesUnlocked.push(awardKey);
    }

    // Save state
    await saveProgressionState(state);

    // Dispatch event for UI notification
    if (typeof window !== 'undefined' && awarded.length > 0) {
      const event = new CustomEvent('operationRewardsAwarded', {
        detail: { operationId, rewards: awarded }
      });
      window.dispatchEvent(event);
    }

    console.log(` Operation rewards awarded:`, awarded);
    return { success: true, awarded };
  } catch (error) {
    console.error(' Failed to award operation rewards:', error);
    return { success: false, awarded: [] };
  }
}

/**
 * Check and award rewards if operation is complete
 * Call this from the Operations page when loading progress
 */
export async function checkAndAwardOperationRewards(
  progress: OperationProgress
): Promise<{ awarded: boolean; rewards: string[] }> {
  if (!progress.completedAt) {
    return { awarded: false, rewards: [] };
  }

  const result = await awardOperationRewards(progress.operationId, progress.userId);
  return { awarded: result.success && result.awarded.length > 0, rewards: result.awarded };
}


